import { BaseController } from 'controllers/BaseController'
import { OnRequestChangeCallback } from 'services/APIs/BaseAPI'
import { InternalAPI } from 'services/APIs/InternalAPI/InternalAPI'
import { PartyEmailQuotationExchangeChannelDto } from 'services/APIs/InternalAPI/internal-api.contracts'

export type EmailConnectionType = 'Forwarding' | 'Microsoft365'

// Compatibility shape until mailbox lifecycle fields are emitted by the API generator.
export type PartyEmailChannel = Omit<PartyEmailQuotationExchangeChannelDto, 'connectionType'> & {
  connectionType?: EmailConnectionType
  provisioningStatus?: string
  verificationStatus?: string
  providerConnectionReference?: string
  subscriptionHealth?: string
  lastError?: string
  forwardingAddress?: string
  connected?: boolean
}

export type PartyEmailChannelStatus = Pick<
  PartyEmailChannel,
  | 'connectionType'
  | 'provisioningStatus'
  | 'verificationStatus'
  | 'providerConnectionReference'
  | 'subscriptionHealth'
  | 'lastError'
  | 'forwardingAddress'
  | 'connected'
>

export type Microsoft365ConnectResponse = PartyEmailChannelStatus & {
  authorizationUrl?: string
}

export const PartyEmailChannelOperations = {
  status: (partyId: string, channelId: string) =>
    `PartyEmailChannel-Status-${partyId}-${channelId}`,
  forwarding: (partyId: string, channelId: string) =>
    `PartyEmailChannel-Forwarding-${partyId}-${channelId}`,
  connectMicrosoft365: (partyId: string, channelId: string) =>
    `PartyEmailChannel-ConnectMicrosoft365-${partyId}-${channelId}`,
  disconnectMicrosoft365: (partyId: string, channelId: string) =>
    `PartyEmailChannel-DisconnectMicrosoft365-${partyId}-${channelId}`,
} as const

class PartyEmailChannelAPI extends InternalAPI {
  constructor(onRequestChange: OnRequestChangeCallback) {
    super('/api/system/partymanagement', onRequestChange)
  }

  status(partyId: string, channelId: string) {
    return this.GetAsync<PartyEmailChannelStatus>({
      id: PartyEmailChannelOperations.status(partyId, channelId),
      relativePath: `/${partyId}/quotation-exchange-channels/${channelId}/email/status`,
    })
  }

  setupForwarding(partyId: string, channelId: string) {
    return this.PostAuthenticatedAsync<PartyEmailChannelStatus>({
      id: PartyEmailChannelOperations.forwarding(partyId, channelId),
      relativePath: `/${partyId}/quotation-exchange-channels/${channelId}/email/forwarding`,
    })
  }

  connectMicrosoft365(partyId: string, channelId: string, returnUrl: string) {
    return this.PostAuthenticatedAsync<Microsoft365ConnectResponse>({
      id: PartyEmailChannelOperations.connectMicrosoft365(partyId, channelId),
      relativePath: `/${partyId}/quotation-exchange-channels/${channelId}/email/microsoft-365/connect`,
      data: { returnUrl },
    })
  }

  disconnectMicrosoft365(partyId: string, channelId: string) {
    return this.DeleteAuthenticatedAsync({
      id: PartyEmailChannelOperations.disconnectMicrosoft365(
        partyId,
        channelId,
      ),
      relativePath: `/${partyId}/quotation-exchange-channels/${channelId}/email/microsoft-365/connection`,
    })
  }
}

export class PartyEmailChannelController extends BaseController<PartyEmailChannelAPI> {
  constructor() {
    super((onRequestChange) => new PartyEmailChannelAPI(onRequestChange))
  }

  status(partyId: string, channelId: string) {
    return this.api
      .status(partyId, channelId)
      .catch((error) => Promise.reject(this.HandleError(error)))
  }

  setupForwarding(partyId: string, channelId: string) {
    return this.api
      .setupForwarding(partyId, channelId)
      .catch((error) => Promise.reject(this.HandleError(error)))
  }

  connectMicrosoft365(partyId: string, channelId: string) {
    return this.api
      .connectMicrosoft365(partyId, channelId, window.location.href)
      .catch((error) => Promise.reject(this.HandleError(error)))
  }

  disconnectMicrosoft365(partyId: string, channelId: string) {
    return this.api
      .disconnectMicrosoft365(partyId, channelId)
      .catch((error) => Promise.reject(this.HandleError(error)))
  }
}
